import Link from 'next/link';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getNotFoundUiContent } from '@/lib/ui-content/not-found-ui-content';
import type { RecoverySuggestion } from '@/lib/not-found/recovery.types';

/**
 * The "we couldn't find that" block.
 *
 * Presentational and chrome-free on purpose: the empty-state branch in
 * app/[...slug]/page.tsx already renders TopMenu / Breadcrumb / MainSearchBar /
 * Footer around it, and the not-found.tsx boundaries supply their own. This
 * component owns only the message and the recovery links.
 *
 * It replaces NoProductsAlert on the dead-URL path. That component is a corner
 * toast: it fades in after 500ms and dismisses itself at 5s, so a visitor who
 * landed on a dead link got a search bar and no stated reason. An explanation
 * the reader can still see ten seconds later has to be in the page, not in a
 * toast.
 */
export const NotFoundMessage = ({
  locale,
  suggestions = [],
}: {
  locale: LocaleDetails;
  suggestions?: RecoverySuggestion[];
}) => {
  const ui = getNotFoundUiContent(locale);
  // The first suggestion is the strongest hit the ranker returned, so it is
  // presented as an answer ("Did you mean…") and the rest as alternatives.
  const [best, ...rest] = suggestions;

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pt-8 text-center max-sm:pt-6">
      <p className="text-xs font-medium uppercase tracking-wider text-muted">{ui.eyebrow}</p>
      <h1 className="mt-3 text-2xl font-medium text-ink max-sm:text-xl">{ui.title}</h1>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted">{ui.body}</p>

      {best && (
        <div className="mt-8 text-left">
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted">
            {ui.bestMatchTitle}
          </p>
          <Link
            href={best.url}
            className="flex items-center justify-between gap-3 rounded-xl border border-hairline bg-card px-4 py-3 transition-colors hover:border-accent/50"
          >
            <span className="min-w-0 truncate text-sm font-medium text-ink">{best.title}</span>
            <span aria-hidden className="shrink-0 text-accent">
              &rarr;
            </span>
          </Link>
        </div>
      )}

      {rest.length > 0 && (
        <div className="mt-6 text-left">
          <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted">
            {ui.suggestionsTitle}
          </p>
          <div className="flex flex-wrap gap-2">
            {rest.map((suggestion) => (
              <Link
                key={suggestion.id}
                href={suggestion.url}
                className="rounded-full border border-hairline px-3.5 py-1.5 text-xs text-ink transition-colors hover:border-accent/50"
              >
                {suggestion.title}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-accent">
        <Link href={`/${locale.path}`}>{ui.goHome}</Link>
        <Link href={`/${locale.path}/sitemap`}>{ui.browseAll}</Link>
        <Link href={`/${locale.path}/contact-us`}>{ui.reportBrokenLink}</Link>
      </div>
    </div>
  );
};
